import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Switch, Tooltip, message } from "antd";
import { EditOutlined, PlusOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import SearchIcon from "../../Assets/reportBuilder/search-icon.svg";
import UsersSharedIcon from "../../Assets/reportBuilder/users-shared.svg";
import UsersPrivateIcon from "../../Assets/reportBuilder/users-private.svg";
import TrashIcon from "../../Assets/reportBuilder/trash-icon.svg";
import EmptyReportFileIcon from "../../Assets/reportBuilder/empty-report-file.svg";
import PlusIcon from "../../Assets/reportBuilder/plus-icon.svg";
import CustomPagination from "../../components/common/CustomPagination";
import DeleteReportModal from "./DeleteReportModal";
import API_MANAGER from "../../API";
import { showReportError } from "./reportToast";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 500;

const TABS = [
  { key: "all", label: "All Reports", type: null },
  // { key: "saved", label: "Saved", type: "saved" },
  { key: "favourites", label: "Favourites", type: "favourite" },
  { key: "shared", label: "Shared with company", type: "shared" },
];

const resolveCreatedBy = (item) => {
  const creator = item?.createdBy || item?.owner || item?.user;
  if (!creator) return "-";
  if (typeof creator === "string") return creator;
  const fullName = [creator?.firstName, creator?.lastName].filter(Boolean).join(" ");
  return fullName || creator?.name || creator?.email || "-";
};

const resolveCreatedById = (item) => {
  const creator = item?.createdBy || item?.owner || item?.user;
  if (typeof creator === "string") return creator;
  return (
    creator?._id ||
    creator?.id ||
    item?.createdById ||
    item?.userId ||
    item?.ownerId ||
    null
  );
};

const normalizeReport = (item, index) => ({
  id: item?._id || item?.id || `report-${index}`,
  name: item?.name || "Untitled report",
  createdBy: resolveCreatedBy(item),
  createdById: resolveCreatedById(item),
  date: item?.createdAt || item?.updatedAt
    ? dayjs(item?.createdAt || item?.updatedAt).format("DD MMM YYYY")
    : "-",
  shared: Boolean(item?.isSharedWithCompany ?? item?.sharedWithCompany ?? item?.isShared),
  favourite: Boolean(item?.isFavourite ?? item?.isFavorite ?? item?.favourite),
});

function ReportsList({ onNewReport, onOpenReport, openingReportId }) {
  const [activeTab, setActiveTab] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const currentUserRole = localStorage.getItem("user");
  const currentUserId =
    localStorage.getItem("userId") || localStorage.getItem("userID");

  const canToggleShare = (report) =>
    currentUserRole === "Root" ||
    (Boolean(report?.createdById) &&
      String(report.createdById) === String(currentUserId));

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchText.trim());
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchReports = useCallback(async () => {
    const tabType = TABS.find((tab) => tab.key === activeTab)?.type;
    const params = { page: currentPage, limit: PAGE_SIZE };
    if (tabType) {
      params.type = tabType;
    }
    if (searchQuery) {
      params.search = searchQuery;
    }

    setLoading(true);
    try {
      const response = await API_MANAGER.getReportsList(params);
      const data = response?.data?.data;
      const list = Array.isArray(data)
        ? data
        : data?.reports || data?.records || data?.result || data?.rows || [];
      const total =
        data?.summary?.totalRecords ??
        data?.totalRecords ??
        data?.totalCount ??
        data?.total ??
        list.length;

      setReports(list.map(normalizeReport));
      setTotalRecords(total);
    } catch (error) {
      showReportError(error, "Failed to load reports");
      setReports([]);
      setTotalRecords(0);
    }
    setLoading(false);
  }, [activeTab, currentPage, searchQuery]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleTabChange = (key) => {
    if (key === activeTab) return;
    setActiveTab(key);
    setCurrentPage(1);
  };

  const patchReport = async (id, changes, revert) => {
    try {
      await API_MANAGER.updateReport(id, changes);
      return true;
    } catch (error) {
      revert();
      showReportError(error, "Failed to update report");
      return false;
    }
  };

  const handleToggleShare = async (id, checked) => {
    const previous = reports;
    setReports((prev) =>
      prev.map((report) => (report.id === id ? { ...report, shared: checked } : report))
    );

    const done = await patchReport(id, { isSharedWithCompany: checked }, () =>
      setReports(previous)
    );
    if (done && activeTab === "shared") {
      fetchReports();
    }
  };

  const handleToggleFavourite = async (id, next) => {
    const previous = reports;
    setReports((prev) =>
      prev.map((report) => (report.id === id ? { ...report, favourite: next } : report))
    );

    const done = await patchReport(id, { isFavourite: next }, () => setReports(previous));
    if (done && activeTab === "favourites") {
      fetchReports();
    }
  };

  const startRename = (report) => {
    setEditingId(report.id);
    setEditingName(report.name);
  };

  const commitRename = async (report) => {
    const nextName = editingName.trim();
    setEditingId(null);

    if (!nextName || nextName === report.name) return;

    const previous = reports;
    setReports((prev) =>
      prev.map((item) => (item.id === report.id ? { ...item, name: nextName } : item))
    );
    await patchReport(report.id, { name: nextName }, () => setReports(previous));
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleteLoading(true);
    try {
      await API_MANAGER.deleteReport(deleteTarget.id);
      message.success("Report successfully deleted.");
      setDeleteTarget(null);

      if (reports.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchReports();
      }
    } catch (error) {
      showReportError(error, "Failed to delete report");
    }
    setDeleteLoading(false);
  };

  const showEmptyState =
    !loading && reports.length === 0 && !searchQuery && activeTab === "all";

  if (showEmptyState) {
    return (
      <div className="report-builder-workspace">
        <div className="empty-state-card">
          <div className="icon-container">
            <img src={EmptyReportFileIcon} alt="" className="file-icon" />
          </div>
          <div className="content-section">
            <h2 className="title">Reports Builder</h2>
            <p className="description">
              Easily build, download and save your secure and fully customisable
              reports.
            </p>
          </div>
          <div className="actions-section">
            <button
              type="button"
              className="new-report-btn"
              onClick={onNewReport}
            >
              <img src={PlusIcon} alt="" className="plus-icon" />
              <span>New Report</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-list-workspace">
      <div className="reports-list-content">
        <div className="reports-list-top">
          <div className="reports-list-heading">
            <h2 className="reports-title">Reports</h2>
            <p className="reports-subtitle">
            Build, save and share reports across your team.
            </p>
          </div>
          <Button
            type="primary"
            className="new-report-btn"
            icon={<PlusOutlined />}
            onClick={onNewReport}
          >
            New Report
          </Button>
        </div>

        <div className="reports-list-toolbar">
          <div className="reports-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`reports-tab ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => handleTabChange(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <Input
            className="reports-search"
            placeholder="Search Reports"
            prefix={<img src={SearchIcon} alt="" className="search-icon" />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
        </div>

        <div className="reports-table">
          <div className="reports-table-header">
            <div className="col-name">Report Name</div>
            <div className="col-created">Created By</div>
            <div className="col-date">Date</div>
            <div className="col-shared">Shared With</div>
            <div className="col-action" />
          </div>

          {loading ? (
            <div className="reports-empty-row">Loading reports...</div>
          ) : reports.length === 0 ? (
            <div className="reports-empty-row">
              {searchQuery ? `No reports found for "${searchQuery}".` : "No reports found."}
            </div>
          ) : (
            reports.map((report) => (
              <div className="reports-table-row" key={report.id}>
                <div className="col-name">
                  {editingId === report.id ? (
                    <Input
                      className="report-name-input"
                      value={editingName}
                      autoFocus
                      onChange={(e) => setEditingName(e.target.value)}
                      onPressEnter={() => commitRename(report)}
                      onBlur={() => commitRename(report)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") setEditingId(null);
                      }}
                    />
                  ) : (
                    <Tooltip title="Click to open report" placement="topLeft">
                      <span
                        role="button"
                        tabIndex={0}
                        className={`report-name-text ${
                          openingReportId === report.id ? "opening" : ""
                        }`}
                        onClick={() => onOpenReport && onOpenReport(report.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            if (onOpenReport) onOpenReport(report.id);
                          }
                        }}
                      >
                        {report.name}
                      </span>
                    </Tooltip>
                  )}
                </div>
                <div className="col-created">{report.createdBy}</div>
                <div className="col-date">{report.date}</div>
                <div className="col-shared">
                  <div className="shared-status">
                    <img
                      src={report.shared ? UsersSharedIcon : UsersPrivateIcon}
                      alt=""
                      className="users-icon"
                    />
                    <span className={report.shared ? "shared-label" : "private-label"}>
                      {report.shared ? "Shared with company" : "Private"}
                    </span>
                  </div>
                  <Tooltip
                    title={
                      canToggleShare(report)
                        ? ""
                        : "Only the report owner can change sharing"
                    }
                  >
                    <Switch
                      className="share-toggle"
                      checked={report.shared}
                      disabled={!canToggleShare(report)}
                      onChange={(checked) => handleToggleShare(report.id, checked)}
                    />
                  </Tooltip>
                </div>
                <div className="col-action">
                  <Tooltip title="Rename report">
                    <button
                      type="button"
                      className="rename-btn"
                      onClick={() => startRename(report)}
                      aria-label="Rename report"
                    >
                      <EditOutlined />
                    </button>
                  </Tooltip>
                  <Tooltip
                    title={report.favourite ? "Remove from favourites" : "Mark as favourite"}
                  >
                    <button
                      type="button"
                      className={`favourite-btn ${report.favourite ? "active" : ""}`}
                      onClick={() => handleToggleFavourite(report.id, !report.favourite)}
                      aria-label="Toggle favourite"
                    >
                      {report.favourite ? <StarFilled /> : <StarOutlined />}
                    </button>
                  </Tooltip>
                  <Tooltip title="Delete report">
                    <button
                      type="button"
                      className="delete-btn"
                      onClick={() => setDeleteTarget(report)}
                      aria-label="Delete report"
                    >
                      <img src={TrashIcon} alt="Delete" />
                    </button>
                  </Tooltip>
                </div>
              </div>
            ))
          )}
        </div>

        {!loading && totalRecords > PAGE_SIZE && (
          <CustomPagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            total={totalRecords}
            itemPerPage={PAGE_SIZE}
          />
        )}
      </div>

      <DeleteReportModal
        open={Boolean(deleteTarget)}
        reportName={deleteTarget?.name}
        loading={deleteLoading}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}

export default ReportsList;
